import { VerticalSlug } from './types'

export interface VerticalProductBadgeProps {
  metadata?: Record<string, any> | null
  vertical?: VerticalSlug
  className?: string
}

export function VerticalProductBadge({
  metadata,
  vertical = 'otro',
  className = '',
}: VerticalProductBadgeProps) {
  if (!metadata || typeof metadata !== 'object' || Object.keys(metadata).length === 0) {
    return null
  }

  const chips: string[] = []

  switch (vertical) {
    case 'inmobiliaria': {
      if (metadata.propertyType) {
        const typeMap: Record<string, string> = {
          apartamento: 'Apartamento',
          casa: 'Casa',
          oficina: 'Oficina',
          terreno: 'Terreno',
          local: 'Local',
          galpon: 'Galpón',
          ph: 'Penthouse',
        }
        chips.push(typeMap[metadata.propertyType] || String(metadata.propertyType))
      }
      if (metadata.operation) {
        chips.push(metadata.operation === 'venta' ? 'Venta' : 'Alquiler')
      }
      if (metadata.rooms !== undefined && metadata.rooms !== null) {
        chips.push(`${metadata.rooms} hab`)
      }
      if (metadata.bathrooms !== undefined && metadata.bathrooms !== null) {
        chips.push(`${metadata.bathrooms} bñ`)
      }
      if (metadata.areaM2) {
        chips.push(`${metadata.areaM2} m²`)
      }
      if (metadata.parkingSpots) {
        chips.push(`${metadata.parkingSpots} estac`)
      }
      if (metadata.zone) {
        chips.push(String(metadata.zone))
      }
      break
    }

    case 'salud': {
      if (metadata.specialty) chips.push(String(metadata.specialty))
      if (metadata.practitioner) chips.push(String(metadata.practitioner))
      if (metadata.durationMin) chips.push(`${metadata.durationMin} min`)
      if (metadata.modality) {
        chips.push(metadata.modality === 'telemedicina' ? 'Telemedicina' : 'Presencial')
      }
      break
    }

    case 'belleza_estetica': {
      if (metadata.durationMin) chips.push(`${metadata.durationMin} min`)
      if (metadata.depositRequired) chips.push(`Abono: ${metadata.depositRequired}`)
      if (metadata.specialist) chips.push(String(metadata.specialist))
      break
    }

    case 'turismo_posadas': {
      if (metadata.capacityPax) chips.push(`Hasta ${metadata.capacityPax} pax`)
      if (metadata.planType) {
        chips.push(metadata.planType === 'todo_incluido' ? 'Todo Incluido' : 'Solo Hospedaje')
      }
      if (metadata.boatTourIncluded) chips.push('Lancha incl.')
      if (metadata.minNights && metadata.minNights > 1) chips.push(`Mín. ${metadata.minNights} noches`)
      break
    }

    case 'restaurante': {
      if (metadata.itemType) {
        const itemTypeMap: Record<string, string> = {
          plato: 'Plato',
          combo: 'Combo',
          bebida: 'Bebida',
          extra: 'Acompañante',
        }
        chips.push(itemTypeMap[metadata.itemType] || String(metadata.itemType))
      }
      if (metadata.prepTimeMin) chips.push(`Prep: ${metadata.prepTimeMin} min`)
      break
    }

    case 'tienda_repuestos': {
      if (metadata.partType) {
        const partMap: Record<string, string> = {
          oem: 'Original OEM',
          reemplazo_a: 'Clase A',
          generico: 'Genérico',
        }
        chips.push(partMap[metadata.partType] || String(metadata.partType))
      }
      if (metadata.brand) chips.push(String(metadata.brand))
      if (metadata.model) chips.push(String(metadata.model))
      if (metadata.yearRange) chips.push(String(metadata.yearRange))
      break
    }

    case 'bodegon_licoreria': {
      if (metadata.temperature === 'frio') chips.push('Bien Frío')
      else if (metadata.temperature === 'natural') chips.push('Temp. Ambiente')
      if (metadata.presentation) chips.push(String(metadata.presentation))
      if (metadata.bundleComboReady) chips.push('Apto Combo')
      break
    }

    case 'ferreteria': {
      if (metadata.gaugeOrSize) chips.push(String(metadata.gaugeOrSize))
      if (metadata.unitOfMeasure) chips.push(String(metadata.unitOfMeasure))
      if (metadata.dispatchType) {
        const dispatchMap: Record<string, string> = {
          retiro_tienda: 'Retiro en tienda',
          moto: 'Despacho moto',
          flete_pesado: 'Flete pesado',
        }
        chips.push(dispatchMap[metadata.dispatchType] || String(metadata.dispatchType))
      }
      break
    }

    case 'servicios': {
      if (metadata.modality) chips.push(String(metadata.modality))
      if (metadata.estimatedDuration) chips.push(String(metadata.estimatedDuration))
      break
    }

    case 'tecnologia': {
      if (metadata.warrantyMonths && !metadata.warranty) chips.push(`${metadata.warrantyMonths}m garantía`)
      if (metadata.condition) chips.push(String(metadata.condition))
      break
    }

    case 'educacion': {
      if (metadata.modality) chips.push(String(metadata.modality))
      if (metadata.duration) chips.push(String(metadata.duration))
      if (metadata.slots) chips.push(`${metadata.slots} cupos`)
      break
    }

    default: {
      for (const [key, val] of Object.entries(metadata)) {
        if (key === 'warranty') continue
        if (val !== undefined && val !== null && val !== '') {
          chips.push(`${key}: ${val}`)
        }
      }
      break
    }
  }

  // Chip de garantía por producto (soporta todas las verticales)
  let warrantyChip: { text: string; className: string } | null = null
  if (metadata.warranty && typeof metadata.warranty === 'object') {
    const w = metadata.warranty
    if (w.hasWarranty === false) {
      warrantyChip = {
        text: w.noWarrantyReason ? `⚠️ Sin garantía (${w.noWarrantyReason})` : '⚠️ Sin garantía',
        className: 'bg-amber-50 text-amber-700 border-amber-200/80',
      }
    } else if (w.hasWarranty === true) {
      const unitMap: Record<string, string> = { horas: 'h', dias: 'd', meses: 'm', anos: 'a' }
      const durStr = w.durationValue ? `${w.durationValue}${unitMap[w.durationUnit] || w.durationUnit || ''}` : ''
      const typeMap: Record<string, string> = { tienda: 'Tienda', fabricante: 'Fábrica', distribuidor: 'Marca' }
      const typeStr = w.warrantyType ? typeMap[w.warrantyType] || w.warrantyType : ''
      const text = `🛡️ ${durStr ? `${durStr} ` : ''}garantía${typeStr ? ` (${typeStr})` : ''}`
      warrantyChip = {
        text,
        className: 'bg-emerald-50 text-emerald-800 border-emerald-200/80 font-semibold',
      }
    }
  }

  if (chips.length === 0 && !warrantyChip) {
    return null
  }

  return (
    <div className={`flex flex-wrap items-center gap-1.5 mt-1.5 ${className}`}>
      {warrantyChip && (
        <span
          className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-md border ${warrantyChip.className}`}
        >
          {warrantyChip.text}
        </span>
      )}
      {chips.map((chip, idx) => (
        <span
          key={idx}
          className="inline-flex items-center text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md font-medium border border-slate-200/70"
        >
          {chip}
        </span>
      ))}
    </div>
  )
}

